/* eslint-disable @typescript-eslint/no-empty-function */
import { ConnectionPositionPair, Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { debouncedSearchLatest } from '@vsolv/utils/rxjs-operators';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { Customer } from '@wsphere/customers/domain';
import { CustomerService } from '@wsphere/customers/web';
import { Distributor } from '@wsphere/distributors/domain';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest, merge, of } from 'rxjs';
import { catchError, map, startWith, switchMap, take, tap } from 'rxjs/operators';

@Component({
  selector: 'ws-provisioning-customer-picker',
  templateUrl: './customer-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: ProvisioningCustomerPickerComponent,
    },
  ],
})
export class ProvisioningCustomerPickerComponent implements ControlValueAccessor, OnDestroy {
  constructor(
    public elementRef: ElementRef,
    private customerSvc: CustomerService,
    private _overlay: Overlay,
    private cdr: ChangeDetectorRef
  ) {}

  /**
   * Force the picker's display to revert to the given value. Used by the parent
   * after a swap-confirmation dialog cancellation — `selectValueChanged` mutates
   * the picker's internal state synchronously when a user clicks a dropdown
   * option, and if the swap is rolled back we need to put the display back.
   */
  resetValue(value: Customer.Model | null) {
    this.value = value;
    this.cdr.markForCheck();
  }

  protected scrollStrategy: ScrollStrategy = this._overlay.scrollStrategies.reposition();
  readonly positions = [
    new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'start', overlayY: 'top' }),
  ];

  @PropertyListener('value') value$ = new ReplaySubject<Customer.Model>(1);
  @Input() value: Customer.Model | null = null;

  @PropertyListener('permissionKey') permissionKey$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() permissionKey?: string;

  @PropertyListener('distributor') distributor$ = new ReplaySubject<Distributor.Model>(1);
  @Input() distributor?: Distributor.Model | null = null;

  @Input() placeholder = 'Find a customer...';
  @Input() required = false;
  @Input() disabled = false;
  @Input() themeColor: ThemeColor = 'primary';

  @Output() openChanged = new EventEmitter<boolean>();
  @Output() valueChanges = new EventEmitter<Customer.Model | null>();

  touched = false;
  overlayOpen = false;

  readonly searchQuery$ = new BehaviorSubject<string>('');
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  readonly loading$ = this.loadingSubject.asObservable();

  private readonly searchTriggers$ = merge(
    this.searchQuery$,
    this.distributor$.pipe(
      startWith(null),
      map(() => this.searchQuery$.value)
    ),
    this.permissionKey$.pipe(map(() => this.searchQuery$.value))
  );

  customers$: Observable<Customer.Model[]> = this.searchTriggers$.pipe(
    debouncedSearchLatest(
      (searchQuery: string) =>
        combineLatest([this.distributor$.pipe(startWith(null)), this.permissionKey$.pipe(startWith(undefined))]).pipe(
          take(1),
          tap(() => this.loadingSubject.next(true)),
          switchMap(([distributor, permissionKey]) =>
            this.customerSvc.listCustomersForSales$({
              search: searchQuery,
              ...(distributor ? { distributorIds: [distributor.id] } : {}),
              ...(permissionKey ? { permissionKey } : {}),
            })
          ),
          map(response => response.items ?? []),
          tap(() => this.loadingSubject.next(false)),
          catchError(() => {
            this.loadingSubject.next(false);
            return of([] as Customer.Model[]);
          })
        ),
      { debounceMs: 300 }
    )
  );

  onChange = (_value: Customer.Model | null) => {};
  onTouched = () => {};

  selectValueChanged(value: Customer.Model) {
    this.markAsTouched();
    this.value?.userId === value?.userId ? (this.value = null) : (this.value = value);
    this.close();
    this.onChange(this.value);
    this.valueChanges.next(this.value);
  }

  open() {
    this.overlayOpen = true;
  }

  close() {
    this.overlayOpen = false;
  }

  toggle() {
    this.overlayOpen = !this.overlayOpen;
  }

  writeValue(value: Customer.Model | null): void {
    this.value = value;
  }

  registerOnChange(onChange: (_value: Customer.Model | null) => {}): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => {}): void {
    this.onTouched = onTouched;
  }

  setDisabledState?(disabled: boolean): void {
    this.disabled = disabled;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  ngOnDestroy(): void {
    this.value$.complete();
    this.searchQuery$.complete();
    this.loadingSubject.complete();
  }
}
